import React, { Fragment, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Location } from '@reach/router';
import ScrollMagic from 'scrollmagic';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import { styles } from 'chaoskit/src/helpers/styles';
import {
  Button,
  Dropdown,
  DropdownHeader,
  List,
  ListItem,
  Reveal,
  OffCanvas,
} from 'chaoskit/src/components';

import { Icon, Link } from '../../components';
import logoWhite from '../../assets/media/logo-white.svg';
import logoPrimary from '../../assets/media/logo-primary.svg';
import logoBlack from '../../assets/media/logo-black.svg';

const Header = ({
  contrast,
  community,
  forceCTAContrast,
  sticky,
  showNav,
  showPrimaryLogo,
  subTitle,
}) => {
  const headerRef = useRef();
  const pageController = useRef();

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const [activeMobileNavItem, setActiveMobileNavItem] = useState();

  useUpdateEffect(() => {
    if (isStuck) {
      headerRef.current.classList.add('is-sticky');
    } else {
      headerRef.current.classList.remove('is-sticky');
    }
  }, [isStuck]);

  const attachScrollListener = () => {
    const $hero = document.querySelector('.hero');
    const $stickyTrigger = document.querySelector('.as-stickyTrigger');

    const offset = () => {
      const $html = document.documentElement;

      if ($hero) {
        return $hero.offsetHeight / 2;
      }

      if ($stickyTrigger) {
        return $stickyTrigger.getBoundingClientRect().top + window.scrollY;
      }

      return $html.clientHeight / 3;
    };

    pageController.current = new ScrollMagic.Controller();

    const headerScene = new ScrollMagic.Scene({
      reverse: true,
      offset: offset(),
    });

    headerScene.on('enter', () => {
      setIsStuck(true);
    });

    headerScene.on('leave', () => {
      setIsStuck(false);
    });

    headerScene.addTo(pageController.current);
  };

  useUpdateEffect(() => {
    const $html = document.documentElement;

    if (isMobileNavOpen) {
      $html.classList.add('has-mobileNav');
    } else {
      $html.classList.remove('has-mobileNav');
    }
  }, [isMobileNavOpen]);

  // Make sure we close mobile nav if resizing from mobile to desktop
  const checkSize = event => {
    if (event.matches) {
      setIsMobileNavOpen(false);
    }
  };

  useEffect(() => {
    const viewportMediumMin = window.matchMedia(
      `(min-width: ${styles.viewport.medium})`
    );

    // Make header "sticky" only if prop supplied; otherwise will be static
    if (sticky) attachScrollListener();

    viewportMediumMin.addListener(checkSize);

    window.onpopstate = event => {
      if (event.srcElement.location.pathname === event.target.location.pathname)
        return;

      setIsMobileNavOpen(false);
      setIsStuck(false);
    };

    return () => {
      if (pageController.current) {
        pageController.current.destroy();
        pageController.current = null;
      }

      viewportMediumMin.removeListener(checkSize);
    };
  }, []);

  // Checks to see if we should expand mobile nav reveal by default
  // @NOTE Waits until OffCanvas is "in DOM" before attempting to calculate and run Reveal animation to avoid jitters
  const checkMobileNavActiveState = () => {
    if (
      ['/resources/', '/blog/', '/community/', '/customers/'].some(
        v => window.location.pathname.indexOf(v) >= 0
      )
    ) {
      setActiveMobileNavItem('resources');
    } else if (
      ['/team/', '/press/', '/careers/'].some(
        v => window.location.pathname.indexOf(v) >= 0
      )
    ) {
      setActiveMobileNavItem('about');
    }
  };

  // Make sure we kill mobile nav styles between routes
  const handleNavClick = () => {
    if (isMobileNavOpen) {
      setIsMobileNavOpen(false);
    }
  };

  const renderCommunityLinks = (linkClass = 'header-link') => (
    <Fragment>
      <ListItem>
        <Link
          className={linkClass}
          to="/community/tutorials/"
          onClick={() => handleNavClick()}
        >
          Tutorials
        </Link>
      </ListItem>
      <ListItem>
        <a
          className={linkClass}
          href="https://gremlin.splashthat.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Events
        </a>
      </ListItem>
      <ListItem>
        <Link
          type="reset"
          className={linkClass}
          to="/community/#talks"
          onClick={() => handleNavClick()}
        >
          Talks
        </Link>
      </ListItem>
      <ListItem>
        <a
          className={linkClass}
          href="https://www.gremlin.com/slack"
          target="_blank"
          rel="noopener noreferrer"
        >
          Slack
        </a>
      </ListItem>
      <ListItem>
        <Link
          type="reset"
          className={linkClass}
          to="/product/"
          onClick={() => handleNavClick()}
        >
          Product
        </Link>
      </ListItem>
    </Fragment>
  );

  const renderMobileNav = () => (
    <OffCanvas
      open={isMobileNavOpen}
      onOffCanvasToggle={() => setIsMobileNavOpen(false)}
      onStart={checkMobileNavActiveState}
      onReverseComplete={() => setActiveMobileNavItem()}
      className="u-contrast"
    >
      <div className="mobileNav-header">
        <Link
          to="/"
          className="header-logo-link"
          onClick={() => handleNavClick()}
        >
          <img src={logoWhite} alt="Gremlin" />
        </Link>
      </div>
      <List className="mobileNav-list">
        {!community ? (
          <Fragment>
            <ListItem>
              <Link
                to="/product/"
                onClick={() => handleNavClick()}
                className="header-mobileLink"
              >
                Product
              </Link>
            </ListItem>
            <ListItem>
              <Link
                to="/pricing/"
                onClick={() => handleNavClick()}
                className="header-mobileLink"
              >
                Pricing
              </Link>
            </ListItem>
            <ListItem>
              <Reveal
                trigger={{
                  label: (
                    <Fragment>
                      Resources{' '}
                      <Icon
                        className="u-ml--small mobileNav-sectionCaret"
                        icon="caret-right"
                        size="small"
                      />
                    </Fragment>
                  ),
                  props: {
                    type: 'reset',
                    className: 'header-mobileLink',
                  },
                }}
                reveal={activeMobileNavItem === 'resources'}
              >
                <List>
                  <ListItem>
                    <Link
                      onClick={() => handleNavClick()}
                      className="header-mobileLink--sub"
                      to="/community/"
                    >
                      Community
                    </Link>
                  </ListItem>
                  <ListItem>
                    <Link
                      onClick={() => handleNavClick()}
                      className="header-mobileLink--sub"
                      to="/community/tutorials/"
                    >
                      Tutorials
                    </Link>
                  </ListItem>
                  <ListItem>
                    <Link
                      onClick={() => handleNavClick()}
                      className="header-mobileLink--sub"
                      to="/customers/"
                    >
                      Customers
                    </Link>
                  </ListItem>
                  <ListItem>
                    <Link
                      onClick={() => handleNavClick()}
                      className="header-mobileLink--sub"
                      to="/blog/"
                    >
                      Blog
                    </Link>
                  </ListItem>
                  <ListItem>
                    <a
                      className="header-mobileLink--sub"
                      href="https://gremlin.splashthat.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Events
                    </a>
                  </ListItem>
                  <ListItem>
                    <a
                      className="header-mobileLink--sub"
                      href="https://www.gremlin.com/slack/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Chaos Slack
                    </a>
                  </ListItem>
                  <ListItem>
                    <Link
                      onClick={() => handleNavClick()}
                      className="header-mobileLink--sub"
                      to="/resources/"
                    >
                      All Resources{' '}
                      <Icon icon="arrow-right" className="u-ml--small" />
                    </Link>
                  </ListItem>
                </List>
              </Reveal>
            </ListItem>
            <ListItem>
              <Reveal
                trigger={{
                  label: (
                    <Fragment>
                      About{' '}
                      <Icon
                        className="u-ml--small mobileNav-sectionCaret"
                        icon="caret-right"
                        size="small"
                      />
                    </Fragment>
                  ),
                  props: {
                    type: 'reset',
                    className: 'header-mobileLink',
                  },
                }}
                reveal={activeMobileNavItem === 'about'}
              >
                <List>
                  <ListItem>
                    <Link
                      onClick={() => handleNavClick()}
                      className="header-mobileLink--sub"
                      to="/team/"
                    >
                      Team
                    </Link>
                  </ListItem>
                  <ListItem>
                    <Link
                      onClick={() => handleNavClick()}
                      className="header-mobileLink--sub"
                      to="/press/about/"
                    >
                      Press
                    </Link>
                  </ListItem>
                  <ListItem>
                    <Link
                      onClick={() => handleNavClick()}
                      className="header-mobileLink--sub"
                      to="/careers/"
                    >
                      Careers
                    </Link>
                  </ListItem>
                </List>
              </Reveal>
            </ListItem>
            <ListItem>
              <Link
                onClick={() => handleNavClick()}
                className="header-mobileLink"
                to="/docs/"
              >
                Docs
              </Link>
            </ListItem>
          </Fragment>
        ) : (
          renderCommunityLinks('header-mobileLink')
        )}
        <ListItem>
          <a
            className="header-mobileLink"
            href="https://app.gremlin.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleNavClick()}
          >
            Login
          </a>
        </ListItem>
        <ListItem className="u-mt--large u-textCenter">
          <Button type="primary" url="/demo?ref=nav" size="small">
            Request a Demo
          </Button>
        </ListItem>
      </List>
    </OffCanvas>
  );

  const renderDesktopNav = () => (
    <Fragment>
      {!community && (
        <nav className="header-nav u-hiddenDown@small">
          <List className="header-navList">
            <ListItem>
              <Link className="header-link" to="/product/">
                Product
              </Link>
            </ListItem>
            <ListItem>
              <Link className="header-link" to="/pricing/">
                Pricing
              </Link>
            </ListItem>
            <ListItem>
              <Location>
                {({ location }) => {
                  const parentClasses = cx(
                    'header-link header-link--dropdown',
                    {
                      'has-activeParent': [
                        '/resources/',
                        '/blog/',
                        '/community/',
                        '/customers/',
                      ].some(v => location.pathname.indexOf(v) >= 0),
                    }
                  );

                  return (
                    <Dropdown
                      showArrow
                      position="center"
                      className="dropdown--resources"
                      trigger={{
                        label: 'Resources',
                        props: {
                          type: 'reset',
                          className: parentClasses,
                        },
                      }}
                    >
                      <div className="row row--largeGutter">
                        <div className="column-6">
                          <DropdownHeader>Getting Started</DropdownHeader>
                          <List className="dropdown-menu">
                            <ListItem>
                              <Link
                                className="header-resources-subLink"
                                to="/community/"
                              >
                                Community
                                <div>
                                  Connect with other engineers who are building
                                  more reliable software with Chaos Engineering.
                                </div>
                              </Link>
                            </ListItem>
                            <ListItem>
                              <Link
                                className="header-resources-subLink"
                                to="/community/tutorials/"
                              >
                                Tutorials
                                <div>
                                  Dive into our step-by-step guides for running
                                  Chaos Experiments in your environment.
                                </div>
                              </Link>
                            </ListItem>
                            <ListItem>
                              <Link
                                className="header-resources-subLink"
                                to="/customers/"
                              >
                                Customers
                                <div>
                                  Learn how teams like yours have used Gremlin
                                  to reduce incidents and improve reliability.
                                </div>
                              </Link>
                            </ListItem>
                          </List>
                        </div>
                        <div className="column-6">
                          <DropdownHeader>Learn More</DropdownHeader>
                          <List className="dropdown-menu">
                            <ListItem>
                              <Link
                                className="header-resources-subLink"
                                to="/blog/"
                              >
                                Blog
                                <div>
                                  Stay up to date with the latest team and
                                  product updates.
                                </div>
                              </Link>
                            </ListItem>
                            <ListItem>
                              <a
                                className="header-resources-subLink"
                                href="https://gremlin.splashthat.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Events
                                <div>
                                  Meet us IRL at the next Chaos Engineering
                                  event in your area.
                                </div>
                              </a>
                            </ListItem>
                            <ListItem>
                              <a
                                className="header-resources-subLink"
                                href="https://www.gremlin.com/slack/"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Chaos Slack
                                <div>
                                  Join the rapidly-growing community of
                                  engineers exploring and practicing Chaos
                                  Engineering
                                </div>
                              </a>
                            </ListItem>
                            <ListItem>
                              <Link className="header-subLink" to="/resources/">
                                Explore All Resources{' '}
                                <Icon
                                  icon="arrow-right"
                                  className="u-ml--small"
                                />
                              </Link>
                            </ListItem>
                          </List>
                        </div>
                      </div>

                      <div className="dropdown-resourcesPanel">
                        <DropdownHeader className="u-textCenter u-mt--remove">
                          Featured Resources
                        </DropdownHeader>
                        <List className="dropdown-menu">
                          <ListItem>
                            <Link
                              className="header-resources-subLink"
                              to="/chaos-engineering/"
                            >
                              What is Chaos Engineering?
                              <div>
                                Go beyond theories and concepts into practical
                                steps with our comprehensive introduction to
                                Chaos Engineering.
                              </div>
                            </Link>
                          </ListItem>
                          <ListItem>
                            <Link
                              className="header-resources-subLink"
                              to="/reliability-calculator?ref=nav"
                            >
                              What's your Reliability Grade?
                              <div>
                                Find out how your different services stack up
                                and get personalized recommendations for how to
                                make them more reliable with our Reliability
                                Calculator.
                              </div>
                            </Link>
                          </ListItem>
                          <ListItem>
                            <Link
                              className="header-resources-subLink"
                              to="/ecommerce-cost-of-downtime/"
                            >
                              The Cost of Downtime for the Top Ecommerce Sites
                              <div>
                                Downtime is expensive, but for ecommerce
                                companies an outage brings business to a
                                standstill.
                              </div>
                            </Link>
                          </ListItem>
                        </List>
                      </div>
                    </Dropdown>
                  );
                }}
              </Location>
            </ListItem>
            <ListItem>
              <Location>
                {({ location }) => {
                  const parentClasses = cx(
                    'header-link header-link--dropdown',
                    {
                      'has-activeParent': [
                        '/team/',
                        '/press/',
                        '/careers/',
                      ].some(v => location.pathname.indexOf(v) >= 0),
                    }
                  );

                  return (
                    <Dropdown
                      showArrow
                      position="center"
                      trigger={{
                        label: 'About',
                        props: {
                          type: 'reset',
                          className: parentClasses,
                        },
                      }}
                    >
                      <DropdownHeader>Learn More</DropdownHeader>
                      <List className="dropdown-menu">
                        <ListItem>
                          <Link className="header-subLink" to="/team/">
                            Team
                          </Link>
                        </ListItem>
                        <ListItem>
                          <Link className="header-subLink" to="/press/about/">
                            Press
                          </Link>
                        </ListItem>
                        <ListItem>
                          <Link className="header-subLink" to="/careers/">
                            Careers
                          </Link>
                        </ListItem>
                      </List>
                    </Dropdown>
                  );
                }}
              </Location>
            </ListItem>
            <ListItem>
              <Link className="header-link" to="/docs/">
                Docs
              </Link>
            </ListItem>
          </List>
        </nav>
      )}
      <div className="header-right u-hiddenDown@small">
        <List className="header-navList">
          {!community ? (
            <ListItem>
              <a
                className="header-link"
                href="https://app.gremlin.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Login{' '}
                <Icon
                  size="small"
                  className="u-ml--xsmall"
                  icon="arrow-right"
                />
              </a>
            </ListItem>
          ) : (
            renderCommunityLinks()
          )}
          <ListItem>
            <Button
              noContrast={!forceCTAContrast}
              type="primary"
              url="/demo?ref=nav"
              size="xsmall"
            >
              Request a Demo
            </Button>
          </ListItem>
        </List>
      </div>
      <div className="header-mobileRight u-hiddenUp@medium">
        <Button
          type="reset"
          onClick={() => setIsMobileNavOpen(true)}
          className="header-mobileNav-toggle"
        >
          <Icon size="large" icon="menu" />
        </Button>
      </div>
    </Fragment>
  );

  const getLogo = () => {
    if (!contrast && !isStuck) {
      if (showPrimaryLogo) {
        return logoPrimary;
      }
      return logoBlack;
    }

    return logoWhite;
  };
  const headerWrapperClasses = cx('header-wrapper', {
    'header-wrapper--noContrast': !contrast,
  });
  const headerClasses = cx('header u-textHeading', {
    'u-contrast': contrast,
  });

  return (
    <Fragment>
      <div className={headerWrapperClasses} ref={headerRef}>
        <div className="container">
          <div className={headerClasses}>
            <div className="header-logo">
              <Link
                to="/"
                className="header-logo-link"
                onClick={() => handleNavClick()}
              >
                <img src={getLogo()} alt="Gremlin" />
              </Link>
              {community && (
                <Link
                  to="/community/"
                  className="header-link header-subTitle"
                  onClick={() => handleNavClick()}
                >
                  Community
                </Link>
              )}
              {subTitle && <div className="header-subTitle">{subTitle}</div>}
            </div>
            {showNav && renderDesktopNav()}
          </div>
        </div>
      </div>
      {showNav && renderMobileNav()}
    </Fragment>
  );
};

Header.propTypes = {
  contrast: PropTypes.bool,
  community: PropTypes.bool,
  forceCTAContrast: PropTypes.bool,
  sticky: PropTypes.bool,
  showNav: PropTypes.bool,
  showPrimaryLogo: PropTypes.bool,
  subTitle: PropTypes.string,
};

Header.defaultProps = {
  contrast: true,
  sticky: true,
  showNav: true,
};

export default Header;
